import type {
  QueryKey,
  UseInfiniteQueryResult,
  UseQueryResult,
} from '@unifyapps/network/react-query';
import type { StoreApi } from 'zustand';
import type { BuildDependencyGraph } from '../../../dependency/manager/types';
import type { ScopeId } from '../../pulse/types';
import type { DataSourceRecordStoreState } from '../../stores/DataSourceRecordStore';
import type { UseCreateDataSourceControllerSliceProps } from './createDataSourceControllerSlice';

export type {
  DeviceDetailsType,
  LocationInStore,
  MobileUserExtraContext,
  UserContextInStore,
  UserExtraGeoLocationContext,
  WebUserExtraContext,
} from '../../stores/app/GlobalAppStateStore/types';

export type CreateDataSourceControllerSliceProps = {
  set: StoreApi<DataSourceControllerState>['setState'];
  get: StoreApi<DataSourceControllerState>['getState'];
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  scopeId: ScopeId;
  // false for RUNNER-mode trees so their dataSourceData writes never tap into Pulse — see createTapBinder.
  capture: boolean;
};

export type QueryResult = UseQueryResult<Awaited<unknown>, unknown>;

export type QueryRequestResult = {
  queryKey?: QueryKey;
  objects?: unknown[];
  rawData?: unknown;
  inputs?: unknown;
} & Partial<Pick<QueryResult, 'error' | 'isLoading' | 'isFetching' | 'data'>> &
  Partial<
    Pick<UseInfiniteQueryResult, 'isFetchingNextPage' | 'hasNextPage' | 'fetchNextPage' | 'refetch'>
  >;

export type DataSourceMetaType = {
  aggregationMetadata?: QueryRequestResult | undefined;
  previewResult?: QueryRequestResult | undefined;
};

export type ManuallyTriggeredDataSourceType = {
  callback: ((...args: unknown[]) => unknown) | undefined;
  dataSourceId: string;
  queryKeyPrefix?: string;
};

export enum DependencyFlowStatus {
  Staging = 'staging',
  Commit = 'commit',
  Finished = 'finished',
}

export type DependencyFlowInstanceType = Record<
  string,
  { status: DependencyFlowStatus } | undefined
>;

export type DataSourceControllerState = {
  dataSources: Record<string, QueryRequestResult | undefined>;
  /**
   * tentative manual registry, where we keep the inflight manual data sources response, once the response is received, we move it to dataSources, here the key will be the requestId
   */
  manualDataSources: Record<string, QueryRequestResult | undefined>;
  dataSourcesMeta: Record<string, DataSourceMetaType | undefined>;
  manuallyTriggeredDataSources: Record<string, ManuallyTriggeredDataSourceType>;
  /**
   * previewTriggeredDataSources is used to track whether a data source has been triggered for preview
   */
  previewTriggeredDataSources: Record<string, boolean | undefined>;
  /**
   * runTriggeredDataSources is used to track whether a data source has been triggered for run
   * this run is user clicking on the run button when saving the data source, it helps us in deciding the content to be shown in the data source preview panel
   */
  runTriggeredDataSources: Record<string, boolean | undefined>;
  /**
   * intra dependencies are the dependencies that track within the same entity -
   * for example, a data source can have dependencies on other data sources
   */
  dataSourceIntraDependencies: BuildDependencyGraph;
  /**
   * dataSourceDependencyFlow is used to manage an id, and it's flow status, whether it's in staging, commit or finished
   */
  dataSourceDependencyFlow: DependencyFlowInstanceType;
};

export type DataSourceControllerActions =
  UseCreateDataSourceControllerSliceProps['dataSourceControllerActions'];
